/**
 * MessageRow — one row in the query message log.
 *
 * Extracted from QueryPanel so the Fleet Query panel (and any future host)
 * can reuse the same DOM/styling without copy-paste. The row itself is
 * presentational: all state lives in the host.
 *
 * Props:
 *   m            — the message:
 *                    { kind: 'statement'|'ok'|'error'|'info',
 *                      status?: 'running'|'ok'|'interrupted'|'error',
 *                      sql?, name?, result?, durationMs?, at, warnings?, id? }
 *   now          — current epoch ms; only read while m.status === 'running'
 *                  (the host ticks it ~4 Hz to drive the elapsed timer)
 *   warnOpen     — Set<id> of message ids whose warning list is expanded
 *   onToggleWarn — (id) => void; toggle warning-list expansion
 *   prefix       — optional JSX rendered before the SQL (e.g. a fleet
 *                  connection chip). Omit in single-connection panels.
 */
import { Show, For } from 'solid-js';
import { copyText } from '../lib/export.js';
import { toast } from '../lib/toast.js';
import { messageText, errorText } from '../lib/messagecopy.js';

// HH:MM:SS.mmm — only used here, so it lives with the component.
function fmtHMS(ms) {
  const t = Math.max(0, Math.round(ms));
  const p2 = (n) => String(n).padStart(2, '0');
  const h = Math.floor(t / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return `${p2(h)}:${p2(m)}:${p2(s)}.${String(t % 1000).padStart(3, '0')}`;
}

// Human-readable elapsed shown beside the raw-seconds counter on a running
// statement — for a long query "2388.4s" is hard to read at a glance, so it also
// gets "(39:48)". Scales up: MM:SS, then HH:MM:SS past an hour, then dd:HH:MM:SS
// past a day.
function fmtElapsed(ms) {
  const t = Math.max(0, Math.floor(ms / 1000)); // whole seconds; the timer ticks ~4 Hz
  const p2 = (n) => String(n).padStart(2, '0');
  const s = t % 60;
  const m = Math.floor(t / 60) % 60;
  const h = Math.floor(t / 3600) % 24;
  const d = Math.floor(t / 86400);
  if (d > 0) return `${d}:${p2(h)}:${p2(m)}:${p2(s)}`;
  if (h > 0) return `${p2(h)}:${p2(m)}:${p2(s)}`;
  return `${p2(m)}:${p2(s)}`;
}

// copyMessage / copyError — shared clipboard actions (toast on success/failure)
// used by both the Shift+click handler here and the host's right-click menu.
export async function copyMessage(m) {
  const text = messageText(m);
  if (!text) return;
  try { await copyText(text); toast.success('Message copied'); }
  catch (e) { toast.error(e.message); }
}
export async function copyError(m) {
  const text = errorText(m);
  if (!text) return;
  try { await copyText(text); toast.success('Error copied'); }
  catch (e) { toast.error(e.message); }
}

export default function MessageRow(props) {
  // Shift+click copies the whole message (incl. the error for a failed
  // statement); a plain click does nothing. Skipped when the click lands on an
  // interactive child (e.g. the warnings chip) so that child's handler runs.
  const onRowMouseDown = (e) => {
    if (!e.shiftKey || e.button !== 0) return;
    if (e.target.closest && e.target.closest('button')) return;
    e.preventDefault(); // suppress text selection from the shift-click
    copyMessage(props.m);
  };
  // Right-click → let the host open a menu (Copy message / Copy error). When no
  // host handler is wired the browser's native menu is left alone.
  const onRowContextMenu = (e) => {
    if (!props.onContextMenu) return;
    props.onContextMenu(e, props.m);
  };
  return (
    <Show
      when={props.m.kind === 'statement'}
      fallback={
        <div class={`query-message query-message-${props.m.kind}`}
             onMouseDown={onRowMouseDown} onContextMenu={onRowContextMenu}
             title="Shift+click to copy · right-click for more">
          <span class="query-message-time mono">{new Date(props.m.at).toLocaleTimeString()}</span>
          <span class="query-message-text">{props.m.text}</span>
        </div>
      }
    >
      <div class={`query-message query-statement query-statement-${props.m.status}`}
           onMouseDown={onRowMouseDown} onContextMenu={onRowContextMenu}
           title="Shift+click to copy · right-click for more">
        <span class="query-message-time mono">{new Date(props.m.at).toLocaleTimeString()}</span>
        <span class={`query-stmt-icon ${props.m.status === 'running' ? 'spin' : ''}`}>
          {props.m.status === 'running' ? '⟳' : props.m.status === 'ok' ? '✓' : props.m.status === 'interrupted' ? '⚠' : '✕'}
        </span>
        <div class="query-stmt-body">
          <div class="query-stmt-sql mono">
            {props.prefix}
            <Show when={props.m.name}>
              <span class="query-stmt-name" title="Named statement (from ~name~ or -- @name: annotation)">{props.m.name}</span>
              {' '}
            </Show>
            {props.m.sql}
          </div>
          <Show
            when={props.m.status === 'running'}
            fallback={
              <div class="query-stmt-result">
                {props.m.result}{props.m.durationMs != null ? ` · ${props.m.durationMs}ms (${fmtHMS(props.m.durationMs)})` : ''}
              </div>
            }
          >
            <div class="query-stmt-result query-stmt-running">
              running… {((props.now - props.m.at) / 1000).toFixed(1)}s ({fmtElapsed(props.now - props.m.at)})
            </div>
          </Show>
          <Show when={props.m.warnings && props.m.warnings.length > 0}>
            <div class="query-stmt-warnings">
              <button
                type="button"
                class="query-warn-chip"
                onClick={() => props.onToggleWarn(props.m.id)}
                title="MySQL raised warnings — a non-strict server may have truncated data silently"
              >
                ⚠ {props.m.warnings.length} warning{props.m.warnings.length === 1 ? '' : 's'}
                <span class="query-warn-caret">{props.warnOpen.has(props.m.id) ? '▾' : '▸'}</span>
              </button>
              <Show when={props.warnOpen.has(props.m.id)}>
                <div class="query-warn-list">
                  <For each={props.m.warnings}>
                    {(w) => (
                      <div class={`query-warn-row query-warn-${(w.level || '').toLowerCase()}`}>
                        <span class="query-warn-level">{w.level}</span>
                        <span class="query-warn-code mono">{w.code}</span>
                        <span class="query-warn-msg">{w.message}</span>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  );
}
